import React from 'react';
import styled from 'styled-components';
import { Row, Col } from 'components/layout';
import BaseCard from 'components/Card';
import StatsCardGroup from '../../components/StatsCard';
import WorkingShiftsChart from './WorkingShiftsChart';
import WorkSegmentsBySpeedChart from './WorkSegmentsBySpeedChart';
import { formatDuration } from 'helpers/date';
import Spin from 'antd/es/spin';
import Tooltip from 'antd/es/tooltip';
import Icon from 'components/Icon';
import {
  SHIFTS_TOOLTIP_TITLE,
  INBOUND_PERFORMANCE_TOOLTIP_TITLE,
} from '../tooltipContent';

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 12px;
`;

const StyledStatsCard = styled(StatsCardGroup)`
  border: 1px solid #EFF2F5;

  & > .ant-card-body {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    gap: 8px;
  }

  & .ant-statistic {
    flex: 1 1 0;
    align-items: center;
    text-align: center;
  }

  & .ant-statistic-title {
    color: #000;
    font-size: 13px;
    white-space: normal;
    line-height: 18px;
    margin-top: 4px;
    min-height: 36px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  & .ant-statistic-content-value-decimal {
    font-size: inherit;
    line-height: inherit;
  }

  & .ant-divider-vertical {
    display: inline-block;
    align-self: stretch;
    border-left: 2px solid #EFF2F5;
    margin: 0 20px;
  }
`;

const formatNumber = (val) => {
  const n = Number(val || 0);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString();
};

const formatOneDecimal = (val) => {
  const n = Number(val || 0);
  if (Number.isNaN(n)) return '0.0';
  return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const Stats = ({ data, loading }) => {
  const shiftsSegmentsStats = (Array.isArray(data?.shifts_segments_stats) && data.shifts_segments_stats[0]) || {};
  const individualStats = (Array.isArray(data?.individual_stats) && data.individual_stats[0]) || {};

  const withPeople = Number(shiftsSegmentsStats.working_shifts_with_people || 0);
  const withoutPeople = Number(shiftsSegmentsStats.working_shifts_no_people || 0);
  const totShifts = Number((data?.tot_shifts?.tot_shifts) || 0);

  const workSegmentsBySpeedData = [
    { type: 'FAST', value: Number(shiftsSegmentsStats.segments_speed_fast || 0) },
    { type: 'NORMAL', value: Number(shiftsSegmentsStats.segments_speed_normal || 0) },
    { type: 'SLOW', value: Number(shiftsSegmentsStats.segments_speed_slot || 0) },
    { type: 'NOT MEASURED', value: Number(shiftsSegmentsStats.segments_speed_not_measured || 0) },
  ];

  return (
    <Spin spinning={loading}>
      <BaseCard>
      <Row gutter={12} align="top" style={{ padding: '8px 8px 0 8px' }}>
        <Col span={10}>
          <SectionTitle>
            Shifts
            <Tooltip title={SHIFTS_TOOLTIP_TITLE} trigger="hover" placement="top">
              <span style={{ marginBottom: '-2px', marginLeft: '6px' }}>
                <Icon
                  name="info"
                  size={16}
                  strokeWidth={'3'}
                  color={'#A4B0BA'}
                />
              </span>
            </Tooltip>
          </SectionTitle>
        </Col>
        <Col span={14}>
          <SectionTitle>
            Inbound Performance
            <Tooltip title={INBOUND_PERFORMANCE_TOOLTIP_TITLE} trigger="hover" placement="top">
              <span style={{ marginBottom: '-2px', marginLeft: '6px' }}>
                <Icon
                  name="info"
                  size={16}
                  strokeWidth={'3'}
                  color={'#A4B0BA'}
                />
              </span>
            </Tooltip>
          </SectionTitle>
        </Col>
      </Row>

      <Row gutter={12} align="middle" style={{ padding: '0 8px' }}>
        <Col span={10}>
          <WorkingShiftsChart
            data={[
              { type: 'working shifts with people', value: withPeople },
              { type: 'working shifts no people', value: withoutPeople },
            ]}
            totShifts={totShifts}
          />
        </Col>
        <Col span={14}>
          <WorkSegmentsBySpeedChart data={workSegmentsBySpeedData} />
        </Col>
      </Row>

      <Row gutter={14}>
        <Col span={10}>
        </Col>
        <Col span={14}>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div style={{ height: 20, borderBottom: '1px solid #F0F0F0', margin: '12px 8px' }} />
        </Col>
      </Row>

      <Row gutter={12} style={{ padding: '0 8px 12px 8px' }} align="middle">
        <Col span={24}>
          <StyledStatsCard
            onlyMiddleDivider
            statistics={[
              {
                title: 'Active Individuals',
                value: formatNumber(individualStats.tot_members),
                titleAlign: ['center', 'bottom'],
              },
              {
                title: 'Average Shifts Per Individual',
                value: formatOneDecimal(individualStats.avg_shifts_per_individual),
                titleAlign: ['center', 'bottom'],
              },
              {
                title: 'Work Segment Count',
                value: formatNumber(shiftsSegmentsStats.tot_segments),
                titleAlign: ['center', 'bottom'],
              },
              {
                title: 'Average CPH',
                value: formatNumber(Math.round(shiftsSegmentsStats.avg_cph_manual || 0)),
                titleAlign: ['center', 'bottom'],
              },
              {
                title: 'Time Saved',
                value: shiftsSegmentsStats.hours_saved !== undefined ? formatDuration(shiftsSegmentsStats.hours_saved * 3600000, false) : '-',
                titleAlign: ['center', 'bottom'],
              },
              {
                title: 'Time Lost',
                value: shiftsSegmentsStats.hours_lost !== undefined ? formatDuration(shiftsSegmentsStats.hours_lost * 3600000, false) : '-',
                titleAlign: ['center', 'bottom'],
              },
            ]}
          />
        </Col>
      </Row>
      </BaseCard>
    </Spin>
  );
};

export default Stats; 
